import React, { useEffect, useState } from "react";
import { PlusCircle, Share2, ChevronRight, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { storyBgStyle } from "@/lib/gradients";
import { useApp } from "@/context/AppContext";
import { SharedPostCard } from "@/components/SharedPostCard";
import { SharedListingCard } from "@/components/marketplace/SharedListingCard";

const SHORT_SHEET = { "--yalola-bottom-sheet-h": "auto" };

const OptionRow = ({ icon: Icon, title, hint, onClick, testId }) => (
  <button type="button" onClick={onClick} className="w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors hover:bg-[color:var(--yalola-surface-alt)]" style={{ borderColor: "var(--yalola-border)" }} data-testid={testId}>
    <span className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Icon size={20} /></span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-semibold">{title}</span>
      <span className="block text-xs leading-snug line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{hint}</span>
    </span>
    <ChevronRight size={16} style={{ color: "var(--yalola-text-muted)" }} />
  </button>
);

// Feuille « Partager » (tap sur ↗) : Ajouter à ma story · Partager dans un groupe (lot 614) · Partager ailleurs (feuille native / lien).
export const ShareSheet = ({ open, onOpenChange, onStory, onGroup, onOther, testId = "share-sheet" }) => {
  const { t } = useApp();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="bottom" className="z-[130]" overlayClassName="z-[130] bg-black/50" style={SHORT_SHEET} data-testid={testId}>
        <DialogHeader><DialogTitle>{t("post.share_sheet_title", "Partager")}</DialogTitle></DialogHeader>
        <DialogBody className="space-y-2">
          <OptionRow icon={PlusCircle} title={t("story.share_add", "Ajouter à ma story")} hint={t("story.share_add_hint", "Une carte de la publication, sur un fond assorti")} onClick={onStory} testId={`${testId}-story`} />
          {onGroup && <OptionRow icon={Users} title={t("post.share_group", "Partager dans un groupe")} hint={t("post.share_group_hint", "Dans le fil d'un groupe dont tu es membre, avec un message")} onClick={onGroup} testId={`${testId}-group`} />}
          <OptionRow icon={Share2} title={t("story.share_other", "Partager ailleurs")} hint={t("story.share_other_hint", "WhatsApp, Messenger, lien…")} onClick={onOther} testId={`${testId}-other`} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

// Aperçu de la story prête (carte sur fond assorti, 4 fonds au choix) → « Publier dans ma story ».
// Lot 697 — `listing` (à la place de `post`) : « Partager la promo en story » — carte de l'annonce en promo du vendeur.
export const ShareToStorySheet = ({ post, listing, open, onOpenChange, onPublished }) => {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [bg, setBg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const targetId = listing ? listing.id : post.id;
  const previewUrl = listing ? `/marketplace/listings/${listing.id}/story-preview` : `/posts/${post.id}/story-preview`;
  useEffect(() => {
    if (!open) return;
    setData(null); setError(null);
    api.get(previewUrl)
      .then(({ data: d }) => { setData(d); setBg(d.backgrounds?.[0] || "navy"); })
      .catch((e) => setError(e?.friendlyMessage || true));
  }, [open, previewUrl]);
  const publish = async () => {
    if (busy || !data) return;
    setBusy(true);
    try {
      const body = listing ? { media_type: "text", shared_listing_id: listing.id, bg } : { media_type: "text", shared_post_id: post.id, bg };
      const { data: r } = await api.post("/stories", body);
      toast.success(t("story.share_done", "Ajoutée à ta story"));
      window.dispatchEvent(new Event("yalola:story_created"));
      if (typeof r?.story?.shares_count === "number") onPublished?.(r.story.shares_count);
      onOpenChange(false);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  const tid = listing ? `story-share-listing-${targetId}` : `story-share-${targetId}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="bottom" className="z-[130]" overlayClassName="z-[130] bg-black/60" style={{ "--yalola-bottom-sheet-h": "92dvh" }} data-testid={tid}>
        <DialogHeader><DialogTitle>{t("story.share_preview_title", "Ta story")}</DialogTitle></DialogHeader>
        <DialogBody className="space-y-4">
          {error && <div className="text-sm text-center py-6" style={{ color: "var(--yalola-danger, #B91C1C)" }} data-testid={`${tid}-error`}>{error === true ? t("common.error", "Erreur") : error}</div>}
          {!error && (
            <div className="mx-auto w-full max-w-[250px] aspect-[9/16] rounded-2xl overflow-hidden relative flex items-center justify-center p-4" style={storyBgStyle(bg)} data-testid={`${tid}-preview`} data-bg={bg || ""}>
              {/* Même largeur que dans la story (StoryCardFrame : 70 %, ≤ 300 px) pour un aperçu fidèle. */}
              {data ? (
                <div className="w-[70%] max-w-[300px]">
                  {listing ? <SharedListingCard sl={data.shared_listing} testId={`${tid}-card`} /> : <SharedPostCard sp={data.shared_post} interactive={false} testId={`${tid}-card`} />}
                </div>
              ) : <div className="h-40 w-full rounded-2xl animate-pulse" style={{ background: "rgba(255,255,255,0.35)" }} />}
            </div>
          )}
          {data?.backgrounds?.length > 0 && (
            <div className="flex items-center justify-center gap-3" data-testid={`${tid}-bgs`}>
              <span className="text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }}>{t("story.share_bg", "Fond")}</span>
              {data.backgrounds.map((b, i) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => setBg(b)}
                  className={`h-9 w-9 rounded-full border-2 transition-transform ${bg === b ? "scale-110 ring-2 ring-offset-2 ring-[color:var(--yalola-primary)]" : "hover:scale-105"}`}
                  style={{ ...storyBgStyle(b), borderColor: "#fff" }}
                  aria-pressed={bg === b}
                  aria-label={`${t("story.share_bg", "Fond")} ${i + 1}`}
                  data-testid={`${tid}-bg-${i}`}
                />
              ))}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button onClick={publish} disabled={busy || !data} className="w-full sm:w-auto rounded-full" data-testid={`${tid}-publish`}>
            <PlusCircle size={16} className="mr-1.5" /> {t("story.share_publish", "Publier dans ma story")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
